import { FC, useState } from "react";
import Restricted from "@/consumer/restricted";
import HeaderBarQuickActionsRestrictedWrapper from "@/components/headerbar/headerBarQuickActionsRestrictedWrapper";
import HeaderBarQuickActionsButton from "@/components/headerbar/headerBarQuickActionsButton";
import TrackedServiceHeaderbarFormModal from "@/components/trackedServiceHeaderBar/trackedServiceHeaderbarFormModal";
import TodoCreateEditModal from "@/components/todos/todoCreateEditModal";
import SubjectOverviewAppointmentCreateModal from "@/components/subjectOverview/subjectOverviewAppointmentCreateModal";
import FileUploadInput from "@/components/forms/inputs/upload";
import Modal from "@/components/modal";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import { useTranslation } from "react-i18next";
import GenericNotification from "@/components/notification/genericNotification";
import AccidentBookQuickReport from "@/components/accidentBook/accidentBookQuickReport";
import { documentWorkspacePermission } from "@/helper/documentPermissions";

interface HeaderBarQuickActionsWrapperProps {
  subdomain: string;
}

const HeaderBarQuickActionsWrapper: FC<HeaderBarQuickActionsWrapperProps> = ({
  subdomain,
}) => {
  const [trackedServiceFormModalisOpen, setTrackedServiceFormModalIsOpen] =
    useState<boolean>(false);
  const [todoCreateModalOpen, setTodoCreateModalOpen] =
    useState<boolean>(false);
  const [appointmentCreateModalOpen, setAppointmentCreateModalOpen] =
    useState<boolean>(false);
  const [uploadModalOpen, setUploadModalOpen] = useState<boolean>(false);
  const [uploadInProgress, setUploadInProgress] = useState(false);
  const [accidenQuickReportModalOpen, setAccidentQuickReportModalOpen] =
    useState<boolean>(false);

  const { handleError, errorModal } = useErrorModal();
  const [successForNotification, setSuccessForNotification] =
    useState<boolean>(false);
  const [notificationMessage, setNotificationMessage] = useState<any>("");
  const [notificationVariant, setNotificationVariant] = useState<
    "success" | "error" | "warning" | "info" | undefined
  >();
  const [showTodoSuccessNotification, setShowTodoSuccessNotification] =
    useState<boolean>(false);
  const { t } = useTranslation();

  const handleOpenTrackedServiceModal = (open: boolean) => {
    setTrackedServiceFormModalIsOpen(open);
  };

  const handleOpenTodoModal = () => {
    setShowTodoSuccessNotification(false);
    setTodoCreateModalOpen(true);
  };

  const handleTodoSuccess = () => {
    setShowTodoSuccessNotification(true);
  };

  const handleOpenAppointmentModal = () => {
    setAppointmentCreateModalOpen(true);
  };
  const handleOpenAccidentBookQuickReportModal = () => {
    setAccidentQuickReportModalOpen(true);
  };
  const handleCloseQuickReportModal = () => {
    setAccidentQuickReportModalOpen(false);
  };

  const handleOpenUploadModal = () => {
    setUploadModalOpen(true);
  };

  const resetStateOnCloseNotification = () => {
    setSuccessForNotification(false);
  };

  const handleNotification = (value: boolean) => {
    setSuccessForNotification(value);
  };
  const handleNotificationMessage = (message: any) => {
    setNotificationMessage(message);
  };
  const handleNotificationVariant = (variant: any) => {
    setNotificationVariant(variant);
  };

  return (
    <HeaderBarQuickActionsRestrictedWrapper subdomain={subdomain}>
      <HeaderBarQuickActionsButton
        handleRecordTrackedService={() => handleOpenTrackedServiceModal(true)}
        handleCreateTodo={handleOpenTodoModal}
        handleCreateAppointment={handleOpenAppointmentModal}
        handleUploadDocument={handleOpenUploadModal}
        handleAccidentQuickReport={handleOpenAccidentBookQuickReportModal}
        subdomain={subdomain}
      />

      <Restricted permissionRequired="frontend_view_trackedservice_headerbar">
        <TrackedServiceHeaderbarFormModal
          modalOpen={trackedServiceFormModalisOpen}
          handleModalOpen={handleOpenTrackedServiceModal}
          handleSetError={handleError}
          handleNotification={handleNotification}
          handleNotificationMessage={handleNotificationMessage}
          handleNotificationVariant={handleNotificationVariant}
          usePersistedSubjectSearchValue={true}
        />
      </Restricted>

      <Restricted permissionRequired="frontend_view_todo_creator">
        <TodoCreateEditModal
          open={todoCreateModalOpen}
          onClose={() => setTodoCreateModalOpen(false)}
          onSuccess={handleTodoSuccess}
          usePersistedSubjectSearchValue={true}
        />
      </Restricted>

      <Restricted permissionRequired="frontend_view_appointment_dashboard">
        {appointmentCreateModalOpen && (
          <SubjectOverviewAppointmentCreateModal
            open={appointmentCreateModalOpen}
            onClose={() => setAppointmentCreateModalOpen(false)}
            usePersistedSubjectSearchValue={true}
          />
        )}
      </Restricted>

      <Restricted permissionRequired={documentWorkspacePermission(subdomain)}>
        <Modal
          open={uploadModalOpen}
          onClose={() => setUploadModalOpen(false)}
          title={t("upload_file")}
          hideDialogActions={true}
          disableCancelButton={uploadInProgress}
          maxWidth="40rem"
        >
          <FileUploadInput
            multiple={true}
            folderId={"00000026-1111-0000-0000-000000000000"}
            name="file"
            evkUpload={false}
            showSubjectSelection={true}
            usePersistedSubjectSearchValue={true}
            onUploadStateChange={setUploadInProgress}
            onCancel={() => {
              setUploadModalOpen(false);
            }}
            handleUploadSuccess={(modalOpen: boolean) => {
              if (modalOpen) {
                setUploadModalOpen(false);
              }
            }}
          />
        </Modal>
      </Restricted>
      <Restricted permissionRequired="frontend_view_quickaction_accident_report">
        <AccidentBookQuickReport
          open={accidenQuickReportModalOpen}
          handleSetError={handleError}
          handleNotification={handleNotification}
          handleNotificationMessage={handleNotificationMessage}
          handleNotificationVariant={handleNotificationVariant}
          handleCloseModal={handleCloseQuickReportModal}
          subdomain={subdomain}
        />
      </Restricted>

      <GenericNotification
        message={successForNotification ? notificationMessage : null}
        variant={notificationVariant}
        handleCloseSnackbar={resetStateOnCloseNotification}
      />
      <GenericNotification
        message={
          showTodoSuccessNotification ? t("todo_created_successfully") : null
        }
        variant="success"
        handleCloseSnackbar={() => setShowTodoSuccessNotification(false)}
      />

      {errorModal}
    </HeaderBarQuickActionsRestrictedWrapper>
  );
};

export default HeaderBarQuickActionsWrapper;
